import { config } from "node-config-ts";
import { useSelector } from "react-redux";
import { AuditLogTypes } from "@constants/audit-log-types";
import { AuditLogLevel } from "@enums/audit-log-level";
import { getQueryParametersState } from "@redux/selectors/query-parameters-selectors";
import { getAsi } from "@redux/selectors/verification-configuration-selectors";
import { Logger } from "@utils/logger";
import { useDeviceDetector } from "./use-device-detector";

/**
 * Audit Logging Service.
 */
export const useAuditLogger = () => {
    const { token, apiUrl } = useSelector(getQueryParametersState);
    const asi = useSelector(getAsi);
    const deviceInfo = useDeviceDetector();
    const baseUrl = process.env.NODE_ENV === "production" ? config.API_URL : "/api";

    const addDeviceDetailsToLog = (): {
        'Device Browser': string;
        'Device Browser Version': string;
        'Device Type': string;
        'Device OS': string;
        'Device OS Version': string;
        'Device User Agent': string;
    } => {
        const userAgent = window.navigator.userAgent;
        const auditLogDetails = {
            'Device Browser': deviceInfo?.browser?.name,
            'Device Browser Version': deviceInfo?.browser?.version,
            'Device Type': deviceInfo?.device?.type,
            'Device OS': deviceInfo?.os?.name,
            'Device OS Version': deviceInfo?.os?.version,
            'Device User Agent': userAgent,
        };
        return auditLogDetails;
    };

    const postTransaction = async (token: string, asi: string, event: string, logLevel: string) => {
        const url = apiUrl ? `${apiUrl}/axn/doc/log` : `${baseUrl}/axn/doc/log`;
        const auditLog = {
            token: token,
            verificationState: event,
            asi: asi,
            detail: event === AuditLogTypes.DCUI_NEW_CAPTURE_SESSION_LAUNCHED ? addDeviceDetailsToLog() : null,
            logLevel,
        };

        try {
            const request = {
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(auditLog),
                method: "POST"
            };

            const response = await fetch(url, request);
            if (response) {
                Logger.info(`[AuditLoggingService]`, response);
            }
        } catch (error) {
            console.log(`Error occurred while using AuditLogger: `, error);
        }
    };

    const writeAuditLog = async (event: string, logLevel: string = AuditLogLevel.INFO) => {
        const newLogLine = `[AuditLoggingService] Audit Log Event = ${token} - ${event}`;
        Logger.info(`${newLogLine}`);
        // Http POST to endpoint
        await postTransaction(token, asi, event, logLevel);
    };

    return {
        writeAuditLog
    };
};